import { useCallback, useEffect, useRef, useState } from "react"
import Head from "next/head"
import { LocationSummary } from "@/types/place"
import {
  CurrentUser,
  CurrentUserProfile,
  PublicUser,
  PublicUserProfile,
} from "@/types/user"
import copy from "copy-to-clipboard"

import { relativeCanonicalUrl } from "@/lib/entity-utils"
import { Translate, useTranslation } from "@/lib/i18n/i18n-context"
import { fullLocationName } from "@/lib/location-utils"
import { useRouter } from "@/lib/router"
import { routes } from "@/lib/routes"
import { generateProfilePageSchema, jsonLdScript } from "@/lib/schema-org-utils"
import { useLazyGetLocationQuery } from "@/lib/storage/api/place-api"
import { selectEmbedded, selectIsXS } from "@/lib/storage/app-slice"
import { showEmailVerifyStep } from "@/lib/storage/auth-thunks"
import { useAppDispatch, useAppSelector } from "@/lib/storage/hooks"
import userSlice, {
  openFollowersDialog,
  selectEmailVerified,
  selectFollowersCount,
  selectFollowingCount,
  selectFollowStatus,
  selectIsUserCrawler,
  selectUserIsEmail,
} from "@/lib/storage/user-slice"
import { toastManager } from "@/lib/toast"
import {
  isFollowingStatus,
  isSearchable,
  userDisplayName,
} from "@/lib/user-utils"
import { BASE_CANONICAL_URL } from "@/lib/utils/constants"
import { singleParam } from "@/lib/utils/url"
import { EditProfileDialog } from "@/components/edit-profile-dialog"
import IconCreate from "@/components/icons/create"
import IconEllipsis from "@/components/icons/ellipsis"
import IconInstagram from "@/components/icons/instagram"
import IconLink from "@/components/icons/link"
import IconLocationPin from "@/components/icons/location-pin"
import IconPencil from "@/components/icons/pencil"
import IconTikTok from "@/components/icons/tiktok"
import IconYouTube from "@/components/icons/youtube"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu"
import { bioMarkdownComponents } from "./bio-markdown"
import { ClampedText } from "./clamped-text"
import { FollowButton } from "./follow-button"
import { IdnDisplayLInk } from "./idn-display-link"
import { PlaceHoverCard } from "./place-hover-card"
import { AriaButton } from "./ui/aria-button"
import { Badge } from "./ui/badge"
import { Button } from "./ui/button"
import { IconButton } from "./ui/icon-button"
import { Link } from "./ui/link"
import { UserAvatar } from "./user-avatar"
import { UserHandle } from "./user-handle"
import { UserName } from "./user-name"

function getWebsiteUrl(url: string) {
  if (!/^https?:\/\//i.test(url)) {
    return `https://${url}`
  }
  return url
}

export function ProfileHeader({
  user,
  userProfile,
  isCurrentUser,
  guidesCount,
  collectionsCount,
}: {
  user: CurrentUser | PublicUser
  userProfile: CurrentUserProfile | PublicUserProfile
  isCurrentUser: boolean
  guidesCount?: number
  collectionsCount?: number
}) {
  const dispatch = useAppDispatch()
  const router = useRouter()
  const rootRef = useRef<HTMLDivElement>(null)
  const isXS = useAppSelector(selectIsXS)
  const [triggerLocationQuery] = useLazyGetLocationQuery()

  const [location, setLocation] = useState<LocationSummary | undefined>()
  const userRef = user.user_ref
  const followStatus = useAppSelector((state) =>
    selectFollowStatus(state, userRef)
  )
  const reduxFollowersCount = useAppSelector((state) =>
    selectFollowersCount(state, userRef)
  )
  const reduxFollowingCount = useAppSelector((state) =>
    selectFollowingCount(state, userRef)
  )
  const [followersCount, setFollowersCount] = useState(reduxFollowersCount)
  const [followingCount, setFollowingCount] = useState(reduxFollowingCount)

  const isCrawler = useAppSelector(selectIsUserCrawler)

  useEffect(() => {
    dispatch(
      userSlice.actions.setFollowCounts({
        userRef,
        followers: isCurrentUser
          ? userProfile.followers_count || 0
          : userProfile.accepted_followers_count || 0,
        following: userProfile.following_count || 0,
      })
    )
  }, [
    dispatch,
    userRef,
    userProfile.followers_count,
    userProfile.following_count,
    userProfile.accepted_followers_count,
    isCurrentUser,
  ])

  useEffect(() => {
    setFollowersCount(reduxFollowersCount)
    setFollowingCount(reduxFollowingCount)
  }, [reduxFollowersCount, reduxFollowingCount])

  const handleOpenFollowersDialog = useCallback(
    (type: "followers" | "following") => {
      dispatch(openFollowersDialog({ user, type }))
    },
    [dispatch, user]
  )

  const locationName = location && fullLocationName(location)
  const acceptedFollower = isFollowingStatus(followStatus)
  const canSeeData = isSearchable(user) || isCurrentUser || acceptedFollower

  const userSchema = isCrawler
    ? generateProfilePageSchema(
        user,
        userProfile,
        {
          postsCount: (guidesCount || 0) + (collectionsCount || 0),
        },
        canSeeData
      )
    : null

  useEffect(() => {
    if (canSeeData && !locationName) {
      void (async () => {
        if ("location" in userProfile && userProfile.location) {
          const locationInfo = await triggerLocationQuery(
            userProfile.location
          ).unwrap()
          setLocation(locationInfo)
        }
      })()
    }
  }, [userProfile, canSeeData, locationName, triggerLocationQuery])

  // `?edit=1` opens the dialog on load, so other pages can link to it
  const [editProfileOpen, setEditProfileOpen] = useState(
    () => isCurrentUser && singleParam(router.query.edit) === "1"
  )

  // Drops `?edit=1` from the URL, and follows the profile to its new URL when
  // a save changed the handle
  const closeEditProfile = (handle: string | undefined) => {
    setEditProfileOpen(false)
    if (handle && (router.query.edit || handle !== user.handle)) {
      const tab =
        singleParam(router.query.tab) === "guides" ? "guides" : undefined
      void router.replace(
        routes.profile.href({ handle, query: { tab } }),
        undefined,
        {
          shallow: handle === user.handle,
        }
      )
    }
  }

  const title = `${user.first_name ? `${userDisplayName(user, true)}` : `@${user.handle}`} | mindtrip`

  return (
    <>
      <Head>
        <title>{title}</title>
        {userSchema && (
          <script
            type="application/ld+json"
            dangerouslySetInnerHTML={{
              __html: jsonLdScript(userSchema),
            }}
          />
        )}
      </Head>
      <div ref={rootRef} className="mx-auto w-full max-w-3xl">
        <div className="flex items-start gap-3 sm:gap-6 md:gap-9">
          <div className="size-(--avatar-size-lg) shrink-0 sm:size-(--avatar-size-2xl)">
            <UserAvatar size={isXS ? "lg" : "2xl"} user={user} />
          </div>
          <div className="flex min-w-0 flex-1 flex-col gap-2">
            <div className="flex items-start gap-3">
              <div className="min-w-0 flex-1">
                <h1 className="mb-px text-lg leading-tight font-semibold">
                  {canSeeData && user.first_name ? (
                    <UserName user={user} />
                  ) : (
                    <UserHandle user={user} />
                  )}
                </h1>
                {canSeeData && user.first_name ? (
                  <UserHandle user={user} className="text-sm text-muted" />
                ) : null}
                {!canSeeData && (
                  <Badge size={2} color="muted" className="mt-1.5">
                    Private
                  </Badge>
                )}
                <div className="mt-2 flex flex-wrap gap-x-2.5">
                  <AriaButton
                    {...(followingCount > 0 &&
                      canSeeData && {
                        onClick: () => handleOpenFollowersDialog("following"),
                        className: "hover:underline",
                      })}
                  >
                    <span className="font-semibold">{followingCount}</span>{" "}
                    <Translate translationKey="followingLabel" />
                  </AriaButton>
                  <AriaButton
                    {...(followersCount > 0 &&
                      canSeeData && {
                        onClick: () => handleOpenFollowersDialog("followers"),
                        className: "hover:underline",
                      })}
                  >
                    <span className="font-semibold">{followersCount}</span>{" "}
                    <Translate
                      translationKey={
                        followersCount === 1
                          ? "followerLabelOne"
                          : "followerLabelOther"
                      }
                    />
                  </AriaButton>
                </div>
              </div>
              <div className="flex flex-col items-end gap-2 md:flex-row">
                {userProfile.shop_link && (
                  <Button
                    href={userProfile.shop_link}
                    size="sm"
                    variant="outline"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <span className="sm:hidden">Shop guides</span>
                    <span className="hidden sm:contents">Shop my guides</span>
                  </Button>
                )}
                {isCurrentUser ? (
                  <ProfileMenu
                    user={user as CurrentUser}
                    onEditProfile={() => setEditProfileOpen(true)}
                  />
                ) : (
                  <FollowButton size="sm" user={user} />
                )}
              </div>
            </div>
            {userProfile.bio && canSeeData && (
              <div className="no-translate leading-5">
                <ClampedText
                  numberOfLines={2}
                  content={userProfile.bio}
                  minimal
                  isMarkdown
                  markdownComponents={bioMarkdownComponents}
                />
              </div>
            )}
            <div className="empty:hidden">
              {userProfile.location &&
                canSeeData &&
                (location ? (
                  <PlaceHoverCard
                    place={location}
                    entitySheetTrackingContext="ProfilePlaceHoverCard"
                  >
                    <Link
                      href={relativeCanonicalUrl(location.canonical_url)}
                      className="flex w-fit items-center gap-1"
                    >
                      <IconLocationPin className="text-[1.25em]" />
                      {locationName}
                    </Link>
                  </PlaceHoverCard>
                ) : (
                  <div className="flex items-center gap-1">
                    <IconLocationPin className="text-[1.25em]" />
                    &nbsp;
                  </div>
                ))}
              {userProfile.website && canSeeData && (
                <div>
                  <Link
                    href={getWebsiteUrl(userProfile.website)}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="-m-2 flex items-center gap-1 p-2 hover:underline"
                  >
                    <IconLink className="text-[1.25em]" />
                    <IdnDisplayLInk url={userProfile.website} />
                  </Link>
                </div>
              )}
              <div className="mt-3 flex items-center gap-1.5 empty:hidden">
                {userProfile.instagram && (
                  <IconButton
                    href={userProfile.instagram}
                    target="_blank"
                    rel="noopener noreferrer"
                    variant="outline"
                    size="xs"
                    icon={{ icon: IconInstagram, size: 16 }}
                    label="Instagram"
                  />
                )}
                {userProfile.tiktok && (
                  <IconButton
                    href={userProfile.tiktok}
                    target="_blank"
                    rel="noopener noreferrer"
                    variant="outline"
                    size="xs"
                    icon={{ icon: IconTikTok, size: 16 }}
                    label="TikTok"
                  />
                )}
                {userProfile.youtube && (
                  <IconButton
                    href={userProfile.youtube}
                    target="_blank"
                    rel="noopener noreferrer"
                    variant="outline"
                    size="xs"
                    icon={{ icon: IconYouTube, size: 16 }}
                    label="YouTube"
                  />
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
      {isCurrentUser && (
        <EditProfileDialog
          open={editProfileOpen}
          onOpenChange={(open) => {
            if (open) setEditProfileOpen(true)
            else closeEditProfile(user.handle)
          }}
          onSaved={(savedUser) => closeEditProfile(savedUser.handle)}
        />
      )}
    </>
  )
}

function ProfileMenu({
  user,
  onEditProfile,
}: {
  user: CurrentUser
  onEditProfile: () => void
}) {
  const t = useTranslation()
  const router = useRouter()
  const dispatch = useAppDispatch()
  const userEmailProvider = useAppSelector(selectUserIsEmail)
  const emailVerified = useAppSelector(selectEmailVerified)
  const isEmbedded = useAppSelector(selectEmbedded)

  const showEmailVerify = () => {
    void dispatch(showEmailVerifyStep())
  }

  return userEmailProvider && !emailVerified ? (
    <Button onClick={showEmailVerify} variant="outline" size="sm">
      <span>
        Verify<span className="hidden sm:contents"> email</span>
      </span>
    </Button>
  ) : (
    <DropdownMenu>
      <DropdownMenuTrigger
        render={
          <IconButton
            variant="outline"
            icon={IconEllipsis}
            label={t("profileActions")}
          />
        }
      />
      <DropdownMenuContent>
        <DropdownMenuItem onClick={onEditProfile} icon={IconPencil}>
          Edit profile
        </DropdownMenuItem>
        <DropdownMenuItem
          onClick={() => {
            const shareUrl = `${BASE_CANONICAL_URL}/profile/${user.handle}?refId=${user.user_ref}`
            if (copy(shareUrl)) {
              toastManager.add({ type: "success", description: "Link copied!" })
            } else {
              toastManager.add({
                type: "error",
                description: "Failed to copy link",
              })
            }
          }}
          icon={IconLink}
        >
          Copy link
        </DropdownMenuItem>
        {!isEmbedded && (
          <DropdownMenuItem
            onClick={() => void router.push("/create")}
            icon={IconCreate}
          >
            Create
          </DropdownMenuItem>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  )
}
